"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { smartFetch } from '../foundation/smartfetch';
import "../../css/pixelated.grid.scss";
import "./nerdjoke.css";
const debug = false;
const TIME_LIMIT = 15;
/**
 * NerdJoke — fetches and displays short Q/A 'nerd' jokes on a timer with controls to pause/advance.
 *
 * Props are primarily internal to the fetching implementation; the component exposes control buttons to pause or fetch the next joke.
 */
/** NerdJoke.propTypes — No props (fetches/show jokes internally).
 * @param {any} [props] - No props are accepted by NerdJoke.
 */
NerdJoke.propTypes = { /** no props */};
export function NerdJoke(props) {
    const [joke, setJoke] = useState({ question: "", answer: "" });
    const timerIntervalRef = useRef(null);
    const jokeIntervalRef = useRef(null);
    const timePassedRef = useRef(0);
    const timePausedRef = useRef(false);
    const timeLeftRef = useRef(TIME_LIMIT);
    const formatTimeLeft = useCallback((time) => {
        if (debug)
            console.log("Formatting Time Left");
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        const formattedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
        return `${minutes}:${formattedSeconds}`;
    }, []);
    const updateTimerDisplay = useCallback(() => {
        if (debug)
            console.log("Updating Timer Display");
        const label = document.getElementById("jokeTimerLabel");
        if (label) {
            label.innerHTML = formatTimeLeft(timeLeftRef.current);
        }
        const elapsedPath = document.getElementById("jokeTimerPathElapsed");
        if (elapsedPath) {
            const myWidth = (((1 / TIME_LIMIT) * timeLeftRef.current) * 100) + "%";
            elapsedPath.style.width = myWidth;
        }
    }, [formatTimeLeft]);
    const loadJoke = useCallback(async () => {
        if (debug)
            console.log("Loading Joke");
        timePassedRef.current = 0;
        timeLeftRef.current = TIME_LIMIT;
        timerIntervalRef.current = null;
        if (jokeIntervalRef.current) {
            clearTimeout(jokeIntervalRef.current);
        }
        jokeIntervalRef.current = setTimeout(() => {
            if (!timePausedRef.current) {
                loadJoke();
            }
        }, TIME_LIMIT * 1000);
        const myURL = "https://nerdjokes.pixelated.tech/prod/nerdjokes?";
        const myURLProps = { command: "%2Fnerdjokes", text: "getjokejson" };
        try {
            const url = myURL + "command=" + myURLProps.command + "&text=" + myURLProps.text;
            const jokeData = await smartFetch(url);
            setJoke(jokeData);
        }
        catch (error) {
            console.error('Failed to fetch joke:', error);
            // Optionally set a fallback joke or handle error
        }
    }, []);
    const startTimer = useCallback(() => {
        if (debug)
            console.log("Starting Timer");
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
        }
        timerIntervalRef.current = setInterval(() => {
            if (!timePausedRef.current) {
                timePassedRef.current += 1;
                timeLeftRef.current = TIME_LIMIT - timePassedRef.current + 1;
                updateTimerDisplay();
            }
        }, 1000);
    }, [updateTimerDisplay]);
    const pauseTimer = () => {
        if (debug)
            console.log("Pausing Timer");
        timePausedRef.current = !timePausedRef.current;
    };
    useEffect(() => {
        loadJoke();
        startTimer();
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
            if (jokeIntervalRef.current) {
                clearTimeout(jokeIntervalRef.current);
            }
        };
    }, [loadJoke, startTimer]);
    return (_jsx("div", { className: "nerd-joke", children: _jsxs("div", { className: "row-12col", children: [_jsx("div", { className: "grid-s1-e5", children: _jsx("div", { className: "left", children: _jsx(JokeButton, { clickFunction: pauseTimer, buttonText: "Pause ||  / Play >" }) }) }), _jsx("div", { className: "grid-s9-e13", children: _jsx("div", { className: "right", children: _jsx(JokeButton, { clickFunction: loadJoke, buttonText: "Next Joke ->" }) }) }), _jsx("div", { className: "joke-timer grid-s1-e13", children: _jsxs("div", { className: "row-12col", children: [_jsx("div", { className: "grid-s1-e11", children: _jsx("svg", { className: "joke-timer-svg", xmlns: "http://www.w3.org/2000/svg", children: _jsx("rect", { id: "jokeTimerPathElapsed" }) }) }), _jsx("div", { className: "grid-s11-e13 center", id: "jokeTimerLabel", children: formatTimeLeft(timeLeftRef.current) })] }) }), _jsxs("div", { className: "joke-text grid-s1-e13", children: [_jsxs("div", { children: [_jsx("span", { className: "label", children: "Q: " }), _jsxs("span", { className: "question", children: [" ", joke.question, " "] })] }), _jsxs("div", { children: [_jsx("span", { className: "label", children: "A: " }), _jsxs("span", { className: "answer", children: [" ", joke.answer, " "] })] })] })] }) }));
}
JokeButton.propTypes = {
    /** Click handler invoked when the button is pressed (e.g., pause or fetch next). */
    clickFunction: PropTypes.func.isRequired,
    /** Label text to render inside the button. */
    buttonText: PropTypes.string.isRequired,
};
function JokeButton(props) {
    /* <div className="jokeButton" onClick={props.clickFunction}>
            {props.buttonText}
        </div> */
    return (_jsx("button", { className: "joke-button", onClick: props.clickFunction, children: props.buttonText }));
}
